import { UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { ICompany, ImpersonationBroker, asApiError, excludeKeysOf } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { queryKeys } from './queryKeys';

export type UpdateCompanyVariables = {
	company: ICompany;
};

/**
 * Save edits to a company — the backend wants the **full** entity (matches the
 * legacy `EntityViewModel.mUpdate` PUT). We strip the deprecated `keyFacts`
 * key for parity with the legacy save path; everything else passes through.
 *
 * On success we invalidate the lead query so the LEAD column re-renders with
 * the new company name / website. The contacts query is unaffected — DM
 * mutations have their own hook.
 */
export function useUpdateCompany(): UseMutationResult<ICompany, Error, UpdateCompanyVariables> {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation<ICompany, Error, UpdateCompanyVariables>({
		mutationFn: async ({ company }) => {
			if (!company.id) {
				throw new Error('Cannot update a company without an id');
			}
			const body = excludeKeysOf(company, ['keyFacts']) as ICompany;
			const result = await userSession.webServiceHelper.callWebServiceAsync<ICompany>(
				ImpersonationBroker.composeApiUrl({ urlPath: `company/${company.id}` }),
				'PUT',
				body
			);
			if (!result.success) {
				throw asApiError(result);
			}
			return result.value;
		},
		onSuccess: (_data, variables) => {
			const id = variables.company.id;
			if (!id) return;
			queryClient.invalidateQueries({ queryKey: queryKeys.focusedView.lead(id) });
		},
	});
}
